import { t } from '@lingui/core/macro'
import { useSaveDocumentMetadataUserEdit } from 'features/knowledgeDocumentViewer/hooks/useSaveDocumentMetadataUserEdit'
import { useSaveDocumentPageMetadataUserEdit } from 'features/knowledgeDocumentViewer/hooks/useSaveDocumentPageMetadataUserEdit'
import type {
  DocumentMetadataUserEditEntry,
  DocumentPageMetadataUserEditEntry,
  DocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import type { FamilyPageMetadataSaveMember } from 'features/knowledgeDocumentViewer/utils/buildFamilyPageMetadataUserEditPuts'
import {
  mergeDocumentMetadataValues,
  mergePageMetadataValues,
} from 'features/knowledgeDocumentViewer/utils/effectiveMetadata'
import { useInvalidateDocumentMetadataUserEditLoads } from 'features/knowledgeDocumentViewer/utils/invalidateDocumentMetadataUserEditLoads'
import { useInvalidateDocumentPageMetadataUserEditLoads } from 'features/knowledgeDocumentViewer/utils/invalidateDocumentPageMetadataUserEditLoads'
import {
  allowsCrossSurfaceMetadataCascade,
  buildDocumentValuesCascadedFromPageEdit,
} from 'features/knowledgeDocumentViewer/utils/sharedMetadataFieldBridge'
import { useGeckoNotifications } from 'features/notifications'
import { useCallback, useState } from 'react'

export type UsePageMetadataUserEditSaveActionsArgs = {
  bqDocumentId: string | undefined
  pageNum: number
  pageEntry: SystemDocumentPageEntry | undefined
  pageUserEdit: DocumentPageMetadataUserEditEntry | undefined
  mutatePageUserEdit: () => Promise<unknown>
  systemDocument?: SystemDocumentEntry
  documentUserEdit?: DocumentMetadataUserEditEntry
  mutateDocumentUserEdit: () => Promise<unknown>
}

export type UsePageMetadataUserEditSaveActionsResult = {
  isSaving: boolean
  savePageMetadata: (
    userValues: DocumentPageMetadataUserEditValues
  ) => Promise<void>
  savePageMetadataFamily: (
    userValues: DocumentPageMetadataUserEditValues,
    members: readonly FamilyPageMetadataSaveMember[]
  ) => Promise<void>
}

export function usePageMetadataUserEditSaveActions({
  bqDocumentId,
  pageNum,
  pageEntry,
  pageUserEdit,
  mutatePageUserEdit,
  systemDocument,
  documentUserEdit,
  mutateDocumentUserEdit,
}: UsePageMetadataUserEditSaveActionsArgs): UsePageMetadataUserEditSaveActionsResult {
  const { savePageMetadataUserEdit, savePageMetadataUserEditFamily } =
    useSaveDocumentPageMetadataUserEdit()
  const {
    saveDocumentMetadataUserEdit,
    saveCascadedDocumentMetadataFromPageFamily,
  } = useSaveDocumentMetadataUserEdit()
  const invalidateDocumentMetadataUserEditLoads =
    useInvalidateDocumentMetadataUserEditLoads()
  const invalidateDocumentPageMetadataUserEditLoads =
    useInvalidateDocumentPageMetadataUserEditLoads()
  const { errorNotification } = useGeckoNotifications()
  const [isSaving, setIsSaving] = useState(false)

  const cascadeAlignedFieldsToDocument = useCallback(
    async (pageNext: DocumentPageMetadataUserEditValues) => {
      if (!bqDocumentId || systemDocument === undefined) return
      if (
        !allowsCrossSurfaceMetadataCascade(
          systemDocument.entry.data.total_pages
        )
      ) {
        return
      }
      const pageBaseline = mergePageMetadataValues(
        pageEntry?.entry.data,
        pageUserEdit?.entry.data.values
      )
      const documentBaseline = mergeDocumentMetadataValues(
        systemDocument.entry.data,
        documentUserEdit?.entry.data.values
      )
      const cascaded = buildDocumentValuesCascadedFromPageEdit({
        pageBaseline,
        pageNext,
        documentBaseline,
      })
      if (cascaded === undefined) return
      await saveDocumentMetadataUserEdit({
        bqDocumentId,
        systemDocument,
        priorEdit: documentUserEdit,
        userValues: cascaded,
      })
      await mutateDocumentUserEdit()
      await invalidateDocumentMetadataUserEditLoads()
    },
    [
      bqDocumentId,
      documentUserEdit,
      invalidateDocumentMetadataUserEditLoads,
      mutateDocumentUserEdit,
      pageEntry?.entry.data,
      pageUserEdit?.entry.data.values,
      saveDocumentMetadataUserEdit,
      systemDocument,
    ]
  )

  const savePageMetadata = useCallback(
    async (userValues: DocumentPageMetadataUserEditValues) => {
      if (!bqDocumentId) {
        errorNotification(t`Cannot save page metadata without a document id`)
        throw new Error('Cannot save page metadata without a document id')
      }
      setIsSaving(true)
      try {
        await savePageMetadataUserEdit({
          bqDocumentId,
          pageNum,
          systemPage: pageEntry,
          priorEdit: pageUserEdit,
          userValues,
        })
        await cascadeAlignedFieldsToDocument(userValues)
        await mutatePageUserEdit()
      } finally {
        setIsSaving(false)
      }
    },
    [
      bqDocumentId,
      cascadeAlignedFieldsToDocument,
      errorNotification,
      mutatePageUserEdit,
      pageEntry,
      pageNum,
      pageUserEdit,
      savePageMetadataUserEdit,
    ]
  )

  const savePageMetadataFamily = useCallback(
    async (
      userValues: DocumentPageMetadataUserEditValues,
      members: readonly FamilyPageMetadataSaveMember[]
    ) => {
      setIsSaving(true)
      try {
        await savePageMetadataUserEditFamily({
          members,
          userValues,
        })
        await saveCascadedDocumentMetadataFromPageFamily({
          sharedPageUserValues: userValues,
          members,
        })
        await mutatePageUserEdit()
        await mutateDocumentUserEdit()
        await invalidateDocumentMetadataUserEditLoads()
        await invalidateDocumentPageMetadataUserEditLoads()
      } finally {
        setIsSaving(false)
      }
    },
    [
      invalidateDocumentMetadataUserEditLoads,
      invalidateDocumentPageMetadataUserEditLoads,
      mutateDocumentUserEdit,
      mutatePageUserEdit,
      saveCascadedDocumentMetadataFromPageFamily,
      savePageMetadataUserEditFamily,
    ]
  )

  return {
    isSaving,
    savePageMetadata,
    savePageMetadataFamily,
  }
}
